/* ===========================================================================
   Duppla Cart — intercepts add-to-cart, renders drawer, syncs with Horizon
   =========================================================================== */
(() => {
  'use strict';

  const drawer = document.querySelector('[data-dp-cart-drawer]');
  if (!drawer) return;

  const itemsEl = drawer.querySelector('[data-cart-items]');
  const emptyEl = drawer.querySelector('[data-cart-empty]');
  const footEl = drawer.querySelector('[data-cart-foot]');
  const recsEl = drawer.querySelector('[data-cart-recs]');
  const countEl = drawer.querySelector('[data-cart-count]');
  const subtotalEl = drawer.querySelector('[data-cart-subtotal]');
  const fsEl = drawer.querySelector('[data-cart-fs]');
  const fsTextEl = drawer.querySelector('[data-cart-fs-text]');
  const fsFillEl = drawer.querySelector('[data-cart-fs-fill]');
  const fsThreshold = parseInt(fsEl?.dataset.threshold || '0', 10);

  const shop = window.DupplaCart || { currency: 'COP', locale: 'es-CO' };
  const fmt = new Intl.NumberFormat(shop.locale || 'es-CO', {
    style: 'currency',
    currency: shop.currency || 'COP',
    maximumFractionDigits: 0,
  });
  const formatMoney = (cents) => fmt.format((cents || 0) / 100);

  /* ---------- Open / close ---------- */
  const open = () => {
    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${window.innerWidth - document.documentElement.clientWidth}px`;
  };
  const close = () => {
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    setTimeout(() => { drawer.hidden = true; }, 450);
  };

  /* ---------- Render ---------- */
  const render = (cart) => {
    const count = cart.item_count || 0;
    if (countEl) countEl.textContent = `${count} ${count === 1 ? 'producto' : 'productos'}`;

    if (count === 0) {
      if (itemsEl) itemsEl.innerHTML = '';
      if (emptyEl) emptyEl.hidden = false;
      if (footEl) footEl.hidden = true;
      if (recsEl) recsEl.hidden = false;
    } else {
      if (emptyEl) emptyEl.hidden = true;
      if (footEl) footEl.hidden = false;
      if (recsEl) recsEl.hidden = false;
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
      if (itemsEl) {
        itemsEl.innerHTML = cart.items
          .map((item) => {
            const image = item.image
              ? `<img src="${item.image}" alt="${escapeAttr(item.product_title)}" loading="lazy">`
              : '';
            const variantLine = item.variant_title && item.variant_title !== 'Default Title'
              ? `<span class="dp-cart__item-variant">${escapeHtml(item.variant_title)}</span>`
              : '';
            return `
              <li class="dp-cart__item" data-key="${escapeAttr(item.key)}">
                <a href="${escapeAttr(item.url)}" class="dp-cart__item-media">${image}</a>
                <div class="dp-cart__item-info">
                  <a href="${escapeAttr(item.url)}" class="dp-cart__item-title">${escapeHtml(item.product_title)}</a>
                  ${variantLine}
                  <div class="dp-cart__item-actions">
                    <div class="dp-cart__item-qty">
                      <button type="button" data-qty="${escapeAttr(item.key)}" data-delta="-1" aria-label="Restar">−</button>
                      <span>${item.quantity}</span>
                      <button type="button" data-qty="${escapeAttr(item.key)}" data-delta="1" aria-label="Sumar">+</button>
                    </div>
                    <span class="dp-cart__item-price">${formatMoney(item.final_line_price)}</span>
                  </div>
                </div>
                <button type="button" class="dp-cart__item-remove" data-remove="${escapeAttr(item.key)}" aria-label="Eliminar ${escapeAttr(item.product_title)}">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                </button>
              </li>
            `;
          })
          .join('');
      }
    }

    updateFreeShipping(cart, count);
    updateHeaderBadge(count);

    // Fade recommended items already in cart
    const inCartIds = new Set(cart.items.map((i) => String(i.product_id)));
    drawer.querySelectorAll('[data-rec-id]').forEach((el) => {
      el.dataset.inCart = inCartIds.has(el.dataset.recId) ? 'true' : 'false';
    });
  };

  const updateFreeShipping = (cart, count) => {
    if (!fsEl || !fsThreshold) return;
    if (count === 0) {
      fsEl.hidden = true;
      return;
    }
    fsEl.hidden = false;
    const total = cart.total_price || 0;
    const remaining = fsThreshold - total;
    const pct = Math.min(100, (total / fsThreshold) * 100);
    fsEl.classList.toggle('is-done', remaining <= 0);
    if (fsTextEl) {
      fsTextEl.innerHTML = remaining > 0
        ? `Te faltan <b>${formatMoney(remaining)}</b> para el <b>envío gratis</b>`
        : `¡Tienes <b>envío gratis</b>! 🎉`;
    }
    if (fsFillEl) fsFillEl.style.width = `${pct}%`;
  };

  const updateHeaderBadge = (count) => {
    // Horizon stores count in cart-bubble elements; update text-count nodes
    document.querySelectorAll('.cart-bubble__text-count').forEach((el) => {
      el.textContent = count > 0 ? String(count) : '';
    });
    document.querySelectorAll('cart-icon').forEach((el) => {
      el.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
    });
  };

  /* ---------- API ---------- */
  const fetchCart = async () => {
    try {
      const res = await fetch(`${shop.cartUrl || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await res.json();
      render(cart);
      return cart;
    } catch (err) {
      console.error('[dp-cart] fetch failed', err);
    }
  };

  const addToCart = async (formData) => {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'Error al agregar al carrito');
    }
    return res.json();
  };

  const updateItemQty = async (key, quantity) => {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    return res.json();
  };

  /* ---------- Event listeners ---------- */
  // Capture-phase click handler: takes over the cart icon before Horizon does
  document.addEventListener(
    'click',
    (e) => {
      const openBtn = e.target.closest(
        '[data-dp-cart-open], [data-testid="cart-drawer-trigger"], .action__cart, cart-drawer-component > button'
      );
      if (openBtn && !openBtn.closest('[data-dp-cart-drawer]')) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        fetchCart().then(open);
      }
    },
    true
  );

  document.addEventListener('click', (e) => {
    // Cart link (falls through when cart_type != drawer, or on pages where component is absent)
    const cartLink = e.target.closest('a[href="/cart"], a[href$="/cart/"]');
    if (cartLink && !cartLink.closest('[data-dp-cart-drawer]') && !window.location.pathname.startsWith('/cart')) {
      e.preventDefault();
      fetchCart().then(open);
      return;
    }
    // Close triggers
    const closeBtn = e.target.closest('[data-dp-cart-close]');
    if (closeBtn) {
      e.preventDefault();
      close();
      return;
    }
    // Quantity +/-
    const qtyBtn = e.target.closest('[data-qty]');
    if (qtyBtn) {
      const key = qtyBtn.dataset.qty;
      const delta = parseInt(qtyBtn.dataset.delta, 10);
      const itemEl = qtyBtn.closest('.dp-cart__item');
      const qtySpan = itemEl?.querySelector('.dp-cart__item-qty span');
      const current = parseInt(qtySpan?.textContent || '1', 10);
      const next = Math.max(0, current + delta);
      drawer.classList.add('dp-is-loading');
      updateItemQty(key, next).then((cart) => {
        render(cart);
        drawer.classList.remove('dp-is-loading');
        broadcast(cart);
      });
      return;
    }
    // Remove item
    const removeBtn = e.target.closest('[data-remove]');
    if (removeBtn) {
      const key = removeBtn.dataset.remove;
      drawer.classList.add('dp-is-loading');
      updateItemQty(key, 0).then((cart) => {
        render(cart);
        drawer.classList.remove('dp-is-loading');
        broadcast(cart);
      });
      return;
    }
  });

  // Escape to close
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
  });

  // Intercept ALL add-to-cart form submits
  document.addEventListener(
    'submit',
    async (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = form.getAttribute('action') || '';
      if (!/\/cart\/add(\?|$)/.test(action)) return;

      e.preventDefault();
      e.stopPropagation();
      const btn = form.querySelector('button[type="submit"], input[type="submit"]');
      let originalText;
      if (btn) {
        originalText = btn.textContent;
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
      }

      try {
        await addToCart(new FormData(form));
        const cart = await fetchCart();
        broadcast(cart);
        open();
      } catch (err) {
        console.error('[dp-cart] add failed', err);
        alert(err.message || 'No se pudo agregar al carrito');
      } finally {
        if (btn) {
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
          if (originalText != null) btn.textContent = originalText;
        }
      }
    },
    true
  );

  // Listen for Horizon cart events (e.g. quick-add from other components)
  document.addEventListener('cart:update', (e) => {
    if (e.detail?.source === 'duppla-cart') return;
    fetchCart();
  });

  /* ---------- Helpers ---------- */
  function broadcast(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cart, source: 'duppla-cart' },
      })
    );
  }
  function escapeHtml(str) {
    return String(str || '').replace(/[&<>"']/g, (s) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[s]));
  }
  function escapeAttr(str) { return escapeHtml(str); }

  /* ---------- Neutralize Horizon's native cart-drawer ---------- */
  const neutralize = () => {
    document.querySelectorAll('cart-drawer-component').forEach((el) => {
      el.removeAttribute('auto-open');
      const dlg = el.querySelector('dialog.cart-drawer__dialog');
      if (dlg && typeof dlg.showModal === 'function') {
        dlg.showModal = () => {};
        dlg.show = () => {};
      }
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', neutralize, { once: true });
  } else {
    neutralize();
  }

  /* ---------- Initial load ---------- */
  fetchCart();
})();
